import {
  WebsiteGeneratorState,
  ImageData,
} from "../../../types/staticWebGenerator";
import { PreviewData } from "../../../types/staticWebGenerator";
import { generateBookingLink } from "../../../utils/bookingLinkUtils";

/**
 * Initialize unified website generator state
 * @param loadedPreviewData - Optional preview data from backend
 * @param businessInfo - Business info for default values
 */
export const initializeWebsiteGeneratorState = (
  loadedPreviewData?: PreviewData | null,
  businessInfo?: any,
): WebsiteGeneratorState => {
  // Default booking link URL
  const defaultBookingLink = generateBookingLink(businessInfo?.id);

  // Default values for colors and fonts (matching UI defaults)
  const COLOR_DEFAULT_BACKGROUND = "#FFD6EE";
  const COLOR_DEFAULT_TEXT = "#0000FF";
  const FONT_DEFAULT_HEADING = "Jacquard 10";
  const FONT_DEFAULT_BODY = "Arial";

  // Initialize all keys to null, except colors and fonts which have defaults
  const initialState: WebsiteGeneratorState = {
    hero: null,
    services: null,
    team: null,
    contact: null,
    footer: null,
    bookingLink: {
      copy: "Book Now",
      useCustomLink: false,
      link: defaultBookingLink,
    },
    // Initialize with default values so they're always in state
    colors: {
      primary: COLOR_DEFAULT_BACKGROUND,
      secondary: COLOR_DEFAULT_TEXT,
    },
    fonts: {
      heading: FONT_DEFAULT_HEADING,
      body: FONT_DEFAULT_BODY,
    },
    gallery: null,
    testimonials: null,
    announcementBanner: null,
    banner: null,
    highlights: null,
    selectedHeroPhoto: null,
    selectedTheme: "generic",
  };

  // If loadedPreviewData exists, map it to unified state
  if (loadedPreviewData) {
    // Map preview data to unified state structure
    // Hero Section
    if (loadedPreviewData.hero_section) {
      initialState.hero = {
        title: loadedPreviewData.hero_section.header,
        description: loadedPreviewData.hero_section.subhead,
        text: loadedPreviewData.hero_section.text || undefined, // Rich text field
        includeBookingButton:
          loadedPreviewData.hero_section.include_book_now_button || false,
        photos: loadedPreviewData.hero_section.image
          ? [{ url: loadedPreviewData.hero_section.image }]
          : [],
      };

      // Set selected hero photo if image exists
      if (loadedPreviewData.hero_section.image) {
        initialState.selectedHeroPhoto = {
          url: loadedPreviewData.hero_section.image,
        };
      }
    }

    // Services
    if (loadedPreviewData.our_services) {
      initialState.services = {
        title: loadedPreviewData.our_services.header,
        subtitle: loadedPreviewData.our_services.subtitle,
        subtext: loadedPreviewData.our_services.subtext,
        default: loadedPreviewData.our_services.default,
        menuNote: loadedPreviewData.our_services.menu_note,
      };
    }

    // Team (preferred) or legacy about_us
    if (loadedPreviewData.team) {
      initialState.team = {
        title: loadedPreviewData.team.title,
        body: loadedPreviewData.team.text,
      };
    } else if (loadedPreviewData.about_us) {
      // Legacy support: map about_us to team
      initialState.team = {
        title: loadedPreviewData.about_us.header,
        body: loadedPreviewData.about_us.subsection,
      };
    }

    // Contact/Find Us
    if (loadedPreviewData.find_us) {
      initialState.contact = {
        ...initialState.contact,
        title: loadedPreviewData.find_us.title,
        subtitle: loadedPreviewData.find_us.subtitle,
        showMap: loadedPreviewData.find_us.google_maps || false,
      };
    }

    // Booking Link
    if (loadedPreviewData.booking_link?.url) {
      initialState.bookingLink = {
        ...initialState.bookingLink,
        link: loadedPreviewData.booking_link.url,
      };
    }

    // Banner
    if (loadedPreviewData.banner) {
      initialState.banner = {
        text: loadedPreviewData.banner.content, // Map content -> text
        textColor: loadedPreviewData.banner.text_color,
        backgroundColor: loadedPreviewData.banner.background_color,
      };
    }

    // Announcement Modal
    if (loadedPreviewData.announcement_modal) {
      initialState.announcementBanner = {
        sectionName: loadedPreviewData.announcement_modal.title,
        body: loadedPreviewData.announcement_modal.body,
        items: loadedPreviewData.announcement_modal.items || [],
        callToAction: loadedPreviewData.announcement_modal.call_to_action,
      };
    }

    // Colors - use preview data if available, otherwise keep defaults
    // IMPORTANT: Preserve empty strings from preview data (don't use || which treats "" as falsy)
    if (loadedPreviewData.color) {
      const defaultColors = initialState.colors!; // Safe because we initialized it above
      initialState.colors = {
        primary:
          loadedPreviewData.color.primary_color !== null &&
          loadedPreviewData.color.primary_color !== undefined
            ? loadedPreviewData.color.primary_color
            : defaultColors.primary,
        secondary:
          loadedPreviewData.color.secondary_color !== null &&
          loadedPreviewData.color.secondary_color !== undefined
            ? loadedPreviewData.color.secondary_color
            : defaultColors.secondary,
      };
    }

    // Fonts - use preview data if available, otherwise keep defaults
    // IMPORTANT: Preserve empty strings from preview data (don't use || which treats "" as falsy)
    if (loadedPreviewData.font) {
      const defaultFonts = initialState.fonts!; // Safe because we initialized it above
      initialState.fonts = {
        heading:
          loadedPreviewData.font.primary_font !== null &&
          loadedPreviewData.font.primary_font !== undefined
            ? loadedPreviewData.font.primary_font
            : defaultFonts.heading,
        body:
          loadedPreviewData.font.secondary_font !== null &&
          loadedPreviewData.font.secondary_font !== undefined
            ? loadedPreviewData.font.secondary_font
            : defaultFonts.body,
      };
    }

    // Footer/Socials
    if (loadedPreviewData.socials) {
      initialState.footer = {
        showSocialIcons: true,
        instagram: loadedPreviewData.socials.instagram_url,
        facebook: loadedPreviewData.socials.facebook_url,
        twitter: loadedPreviewData.socials.twitter_url,
        tiktok: (loadedPreviewData.socials as any).tiktok_url,
      };
    }

    // Gallery (optional)
    if (loadedPreviewData.gallery) {
      initialState.gallery = {
        title: loadedPreviewData.gallery.title,
        gridLayout: loadedPreviewData.gallery.grid_layout,
        items: loadedPreviewData.gallery.items
          ? loadedPreviewData.gallery.items.map((item, index) => ({
              id: index,
              url: item.image_url,
              selected: true,
              title: item.title,
              description: item.description,
            }))
          : [],
      };
    }

    // Testimonials (optional)
    if (loadedPreviewData.testimonials) {
      initialState.testimonials = {
        title: loadedPreviewData.testimonials.title || "",
        description: loadedPreviewData.testimonials.subtitle || "", // Map subtitle -> description
        body: loadedPreviewData.testimonials.text || "", // Map text -> body
        cards: loadedPreviewData.testimonials.testimonials
          ? loadedPreviewData.testimonials.testimonials.map(
              (testimonial, index) => ({
                id: index + 1,
                name: testimonial.name || "",
                title: testimonial.source || "",
                text: testimonial.content || "",
                image: testimonial.picture
                  ? { url: testimonial.picture }
                  : undefined,
              }),
            )
          : [],
      };
    }

    // Highlights (optional)
    if (loadedPreviewData.service_highlights?.highlights) {
      initialState.highlights = {
        sectionName: "",
        items: loadedPreviewData.service_highlights.highlights.map(
          (highlight, index) => ({
            id: index,
            icon: highlight.icon,
            title: highlight.title,
            body: highlight.body,
          }),
        ),
      };
    }
  }

  return initialState;
};
